import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import JsonLd from "@/components/base/JsonLd";
import { leakEntries } from "@/mocks/leaks";

const SITE = "https://esjxdt.cn";

const levelTone: Record<string, string> = {
  已核验: "bg-primary-500 text-background-50",
  多方印证: "bg-accent-500 text-foreground-950",
  待核验: "bg-secondary-500 text-background-50",
  线索汇总: "bg-background-200 text-foreground-800",
};

export default function LeakDetail() {
  const { id } = useParams();
  const entry = leakEntries.find((item) => item.id === id);

  if (!entry) {
    return (
      <SiteLayout>
        <title>线索未找到 | 深夜爆料</title>
        <meta name="robots" content="noindex, follow" />
        <div className="pt-16">
          <div className="wrap flex min-h-[60vh] flex-col items-center justify-center gap-4 py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-file-unknow-line text-2xl" />
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">这条线索不存在</h1>
            <p className="max-w-md text-sm leading-relaxed text-foreground-600">
              它可能已经被更正、合并到其他条目，或者链接输入有误。
            </p>
            <Link
              to="/leaks"
              className="btn-base bg-primary-500 px-5 py-2.5 text-background-50 hover:bg-primary-600"
            >
              返回线索库
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = leakEntries
    .filter((item) => item.id !== entry.id && item.categorySlug === entry.categorySlug)
    .concat(leakEntries.filter((item) => item.id !== entry.id && item.categorySlug !== entry.categorySlug))
    .slice(0, 3);

  const detailJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: entry.title,
        articleSection: entry.category,
        inLanguage: "zh-CN",
        datePublished: entry.date,
        description: entry.summary,
        url: `${SITE}/leaks/${entry.id}`,
        image: entry.cover,
        publisher: {
          "@type": "Organization",
          name: "深夜爆料编辑室",
          url: `${SITE}/`,
        },
        isPartOf: { "@type": "WebSite", name: "深夜爆料", url: `${SITE}/` },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "线索库", item: `${SITE}/leaks` },
          { "@type": "ListItem", position: 3, name: entry.title, item: `${SITE}/leaks/${entry.id}` },
        ],
      },
    ],
  };

  return (
    <SiteLayout>
      <title>{`${entry.title} | 深夜爆料线索`}</title>
      <meta name="description" content={entry.summary.slice(0, 150)} />
      <meta name="keywords" content={`深夜爆料,${entry.category},${entry.tags.join(",")}`} />
      <link rel="canonical" href={`${SITE}/leaks/${entry.id}`} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={`${entry.title} | 深夜爆料`} />
      <meta property="og:description" content={entry.summary.slice(0, 150)} />
      <meta property="og:url" content={`${SITE}/leaks/${entry.id}`} />
      <meta property="og:image" content={entry.cover} />
      <meta name="twitter:card" content="summary_large_image" />
      <JsonLd data={detailJsonLd} />

      <div className="pt-16">
        <section className="ink-panel relative overflow-hidden">
          <div className="grid-rule absolute inset-0 opacity-30" />
          <div className="wrap relative py-10 md:py-14">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 font-label text-xs text-background-400">
              <Link to="/" className="transition-colors hover:text-accent-300">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/leaks" className="transition-colors hover:text-accent-300">
                线索库
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to={`/leaks?cat=${entry.categorySlug}`}
                className="transition-colors hover:text-accent-300"
              >
                {entry.category}
              </Link>
            </nav>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className={`rounded px-2.5 py-1 font-label text-xs ${levelTone[entry.level] ?? levelTone["线索汇总"]}`}>
                {entry.level}
              </span>
              <span className="font-label text-xs text-background-400">编号 {entry.code}</span>
              <span className="font-label text-xs text-background-400">
                {entry.date} {entry.time} 发布
              </span>
            </div>

            <h1 className="mt-5 max-w-4xl font-heading text-2xl font-black leading-snug text-background-50 md:text-4xl">
              {entry.title}
            </h1>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 font-label text-xs text-background-300">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                {entry.source}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {entry.views.toLocaleString("zh-CN")} 阅读
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-chat-3-line" />
                {entry.replies} 讨论
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line" />
                约 {entry.readTime}读
              </span>
            </div>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="wrap py-10 md:py-14">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
              <article className="lg:col-span-8">
                <div className="h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
                  <img
                    src={entry.cover}
                    alt={`${entry.title} - 深夜爆料线索现场配图`}
                    title={`${entry.title} - 深夜爆料`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>

                <p className="mt-8 border-l-2 border-primary-500 pl-4 font-heading text-base font-semibold leading-relaxed text-foreground-800 md:text-lg">
                  {entry.summary}
                </p>

                <div className="mt-10">
                  {entry.body.map((para, index) => (
                    <p
                      key={index}
                      className="mb-5 text-[15px] leading-[1.9] text-foreground-800"
                    >
                      {para}
                    </p>
                  ))}
                </div>

                <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6">
                  <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                      <i className="ri-list-check-2" />
                    </span>
                    关键信息点
                  </h2>
                  <ul className="mt-5 space-y-3">
                    {entry.keyPoints.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                        <span className="mt-2 flex h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-10">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">核验时间线</h2>
                  <ol className="mt-6 border-l border-background-300 pl-6">
                    {entry.timeline.map((item) => (
                      <li key={item.date} className="relative pb-7 last:pb-0">
                        <span className="absolute -left-[27px] top-1 flex h-3 w-3 items-center justify-center rounded-full border-2 border-background-50 bg-primary-500" />
                        <span className="font-label text-xs tracking-wide text-primary-600">
                          {item.date}
                        </span>
                        <p className="mt-1 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-2">
                  <span className="font-label text-xs text-foreground-500">标签</span>
                  {entry.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-3 py-1.5 font-label text-xs text-secondary-900"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </article>

              <aside className="lg:col-span-4">
                <div className="sticky top-24 flex flex-col gap-6">
                  <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                    <h2 className="font-heading text-base font-bold text-foreground-950">
                      这条线索怎么来的
                    </h2>
                    <dl className="mt-5 space-y-4">
                      <div>
                        <dt className="font-label text-xs text-foreground-500">来源描述</dt>
                        <dd className="mt-1 text-sm text-foreground-800">{entry.source}（已脱敏）</dd>
                      </div>
                      <div>
                        <dt className="font-label text-xs text-foreground-500">可信等级</dt>
                        <dd className="mt-1 flex items-center gap-2 text-sm text-foreground-800">
                          <span className={`rounded px-2 py-0.5 font-label text-xs ${levelTone[entry.level] ?? levelTone["线索汇总"]}`}>
                            {entry.level}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="font-label text-xs text-foreground-500">发布时间</dt>
                        <dd className="mt-1 text-sm text-foreground-800">
                          {entry.date} {entry.time}
                        </dd>
                      </div>
                    </dl>
                    <p className="mt-5 border-t border-background-200 pt-4 text-xs leading-relaxed text-foreground-500">
                      等级反映的是当前掌握的印证程度，不代表最终结论。若你有相反证据，欢迎从投稿入口提交。
                    </p>
                  </div>

                  <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                    <h2 className="font-heading text-base font-bold text-foreground-950">相关内容</h2>
                    <ul className="mt-4 flex flex-col divide-y divide-background-200">
                      {related.map((item) => (
                        <li key={item.id}>
                          <Link to={`/leaks/${item.id}`} className="group block py-4">
                            <span className="flex items-center gap-2 font-label text-[11px] text-foreground-500">
                              <i className="ri-price-tag-3-line" />
                              {item.category}
                            </span>
                            <span className="mt-1.5 block font-heading text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                              {item.title}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to="/#submit"
                    className="flex items-center justify-between gap-3 rounded-lg bg-primary-500 p-6 text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <span>
                      <span className="block font-heading text-base font-bold">我有补充信息</span>
                      <span className="mt-1 block font-label text-xs text-primary-100">
                        匿名提交，不记录身份
                      </span>
                    </span>
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50/20">
                      <i className="ri-arrow-right-line text-lg" />
                    </span>
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}